import React, { useEffect, useRef, useState } from 'react';

import { errorMessage } from '../../services/apiError';
import { loopService } from '../../services/loopService';
import type { BrowserConnectionResult, BrowserProfile } from '../../services/loopService';
import { browserAgentDisplayText } from '../../utils/browserAgentPresentation';

export const LoopConnectionTest: React.FC<{ profile: BrowserProfile; disabled?: boolean }> = ({
  profile, disabled,
}) => {
  const request = useRef<globalThis.AbortController | null>(null);
  const [pending, setPending] = useState(false);
  const [result, setResult] = useState<BrowserConnectionResult | null>(null);

  useEffect(() => () => { request.current?.abort(); }, []);

  // eslint-disable-next-line max-statements
  const test = async () => {
    if (request.current || disabled) return;
    const controller = new globalThis.AbortController();

    request.current = controller; setPending(true); setResult(null);
    try {
      const response = await loopService.testProfileConnection(profile.id, controller.signal);

      if (!controller.signal.aborted) setResult(response);
    } catch (reason) {
      if (!controller.signal.aborted) setResult({ status: 'unavailable',
        message: errorMessage(reason, 'The connection could not be tested. Please try again.') });
    } finally {
      if (!controller.signal.aborted) { request.current = null; setPending(false); }
    }
  };

  return <div className="loop-connection-test">
    <button type="button" disabled={disabled || pending} aria-label={`Test connection to ${profile.name}`}
      onClick={() => void test()}><i className={`fa-solid ${pending ? 'fa-spinner fa-spin' : 'fa-plug'}`}
        aria-hidden="true" /> {pending ? 'Testing…' : 'Test connection'}</button>
    {pending && <p className="loop-caption loop-muted" role="status">Checking {profile.name}…</p>}
    {result && <p className={`loop-connection-test__result is-${result.status}`} role="status">
      <span className={`loop-dot${result.status === 'connected' ? ' is-active' : ''}`} aria-hidden="true" />
      {browserAgentDisplayText(result.message)}
      {result.status === 'connected' && typeof result.latency_ms === 'number'
        && <small> · {Math.round(result.latency_ms)} ms</small>}
    </p>}
  </div>;
};
